import { ReactNode, useEffect } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { GuestAuthBar, NavbarSimple } from './NavBar';
import { useNavbar } from '../hooks/useNavbar';
import { useLocation } from 'react-router-dom';
import { clientRoutes } from '../ApiPaths';
import {
  invalidateAuthenticatedProfileLoads,
  loadAuthenticatedUserProfile,
  storeSsoReturn,
} from '../services/Authentication';
import { useStore } from '../store/Store';
import { NotificationBell } from '@/components/NotificationBell';
import { CompareTray } from '@/components/CompareProducts/CompareTray';
import { useCompareStore } from '@/store/compareStore';

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * Main application layout component
 * Handles navbar and main content area layout
 */
export function AppLayout({ children }: AppLayoutProps) {
  const { isNavbarOpen, setIsNavbarOpen, isSmallScreen } = useNavbar();
  const session = useSessionContext();
  const showNotificationBell = !session.loading && session.doesSessionExist;
  const sessionUserId = showNotificationBell ? session.userId : '';
  const location = useLocation();
  const compareCount = useCompareStore((s) => s.items.length);
  const compareMinimized = useCompareStore((s) => s.minimized);
  const isDropshippingPage = location.pathname === clientRoutes.dropshipping;
  const isSsoDemoPage = location.pathname === clientRoutes.ssoDemo;
  const hideNavbar =
    location.pathname.includes('/embed') || isDropshippingPage || isSsoDemoPage;
  const showGuestCorner =
    !hideNavbar && !isNavbarOpen && !session.loading && !session.doesSessionExist;
  const showCompareTray = !hideNavbar && compareCount > 0 && location.pathname !== clientRoutes.compare;
  const comparePad = showCompareTray ? (compareMinimized ? 64 : 110) : undefined;

  // SSO: when user lands on login/auth with return_url and state, store for post-login redirect
  useEffect(() => {
    const isLoginOrAuth =
      location.pathname === clientRoutes.login ||
      location.pathname === clientRoutes.loginVerifyEmail ||
      location.pathname === '/login' ||
      location.pathname.startsWith('/auth');
    if (!isLoginOrAuth) return;
    const params = new URLSearchParams(location.search);
    const return_url = params.get('return_url');
    const state = params.get('state');
    if (return_url && state) {
      storeSsoReturn(return_url, state);
    }
  }, [location.pathname, location.search]);

  // Keep the persisted profile aligned with the SuperTokens session after account switches.
  useEffect(() => {
    if (session.loading) return;
    if (!sessionUserId) {
      if (useStore.getState().userProfile._id) {
        invalidateAuthenticatedProfileLoads();
        useStore.getState().reset();
      }
      return;
    }
    if (useStore.getState().userProfile._id !== sessionUserId) {
      void loadAuthenticatedUserProfile(sessionUserId);
    }
  }, [session.loading, sessionUserId]);

  // Keep page headers clear of the fixed sign-up control without inserting a blank band.
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => {
      root.style.removeProperty('--guest-corner-pad');
      root.style.removeProperty('--guest-corner-top');
    };
    if (showGuestCorner) {
      root.style.setProperty('--guest-corner-pad', '148px');
      root.style.setProperty('--guest-corner-top', '38px');
    } else {
      clear();
    }
    return clear;
  }, [showGuestCorner]);

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {!hideNavbar && (
        <NavbarSimple
          isOpen={isNavbarOpen}
          onToggle={setIsNavbarOpen}
          isSmallScreen={isSmallScreen}
        />
      )}
      <div
        style={{
          flexGrow: 1,
          width: !hideNavbar && isNavbarOpen && !isSmallScreen ? 'calc(100% - 300px)' : '100%',
          overflowX: 'hidden',
          transition: 'width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), padding-bottom 0.22s ease',
          marginLeft: !hideNavbar && isSmallScreen && !isNavbarOpen ? '0' : 'auto',
          paddingBottom: comparePad,
        }}
      >
        {!hideNavbar && !isNavbarOpen && <GuestAuthBar />}
        {!hideNavbar && showNotificationBell && (
          <div
            style={{
              position: 'fixed',
              top: 12,
              right: 16,
              // Below Mantine Modal (~200) so mobile modal close (X) stays clickable
              zIndex: 150,
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.12)',
              backgroundColor: 'color-mix(in srgb, var(--mantine-color-body) 80%, transparent)',
              backdropFilter: 'blur(10px)',
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 10,
              padding: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <NotificationBell />
          </div>
        )}
        {children}
        {!hideNavbar && <CompareTray />}
      </div>
    </div>
  );
}
